// components/omore/OmoreModerationQueue.tsx
// Review flagged Omore messages with real-time updates

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    fetchModerationQueue,
    subscribeToFlaggedMessages,
    reviewFlaggedMessage
} from '../../services/omoreInteractionService';
import { exportModerationQueueToCSV, downloadCSV } from '../../services/omoreExportService';
import { SeverityColors, FlagSeverityMap } from '../../types/OmoreInteractionTypes';
import type { FlaggedMessage, ModerationQueueFilter, ModerationAction } from '../../types/OmoreInteractionTypes';
import './OmoreModerationQueue.css';

const FLAG_CATEGORIES = ['self_harm', 'violence', 'sexual', 'harassment', 'hate', 'inappropriate', 'prompt_injection', 'other'] as const;

const CATEGORY_LABELS: Record<string, string> = {
    self_harm: 'Self-Harm',
    violence: 'Violence',
    sexual: 'Sexual',
    harassment: 'Harassment',
    hate: 'Hate',
    inappropriate: 'Inappropriate',
    prompt_injection: 'Prompt Injection',
    other: 'Other',
};

export const OmoreModerationQueue: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [items, setItems] = useState<FlaggedMessage[]>([]);
    const [liveCount, setLiveCount] = useState(0);
    const [selectedItem, setSelectedItem] = useState<FlaggedMessage | null>(null);
    const [reviewNotes, setReviewNotes] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // Filter state
    const [reviewStatus, setReviewStatus] = useState<'unreviewed' | 'reviewed' | 'all'>('unreviewed');
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Real-time subscription for live badge count
    useEffect(() => {
        const unsubscribe = subscribeToFlaggedMessages((messages) => {
            setLiveCount(messages.length);
        });
        return () => unsubscribe();
    }, []);


    const loadQueue = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const filter: ModerationQueueFilter = {
                reviewStatus,
                categories: selectedCategories.length > 0 ? (selectedCategories as any) : undefined,
                page: currentPage,
                pageSize: 25,
                sortBy: 'flaggedAt',
                sortDirection: 'desc',
            };
            const result = await fetchModerationQueue(filter);
            setItems(result.items);
            setTotalPages(result.totalPages);
            setTotalCount(result.totalCount);
        } catch (err: any) {
            console.error('Error loading moderation queue:', err);
            setError(err.message || 'Failed to load queue');
        } finally {
            setLoading(false);
        }
    }, [reviewStatus, selectedCategories, currentPage]);

    // Load queue whenever filters change
    useEffect(() => {
        loadQueue();
    }, [loadQueue]);

    const toggleCategory = (cat: string) => {
        setSelectedCategories(prev =>
            prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
        );
        setCurrentPage(0);
    };

    const handleReview = async (action: ModerationAction) => {
        if (!selectedItem || !currentUser?.email) return;
        try {
            setActionLoading(true);
            await reviewFlaggedMessage(selectedItem.id, action, currentUser.email, reviewNotes);
            setSelectedItem(null);
            setReviewNotes('');
            await loadQueue();
        } catch (err: any) {
            console.error('Error reviewing message:', err);
            alert('Failed to submit review');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    const formatTimestamp = (ts: any): string => {
        if (!ts) return 'N/A';
        try {
            const date = ts.toDate ? ts.toDate() : new Date(ts);
            return date.toLocaleString();
        } catch {
            return 'N/A';
        }
    };

    return (
        <div className="omore-mod-container">
            <header className="omore-mod-header">
                <div className="header-content">
                    <div>
                        <h1>
                            Omore Moderation Queue
                            {liveCount > 0 && <span className="live-badge">{liveCount}</span>}
                        </h1>
                        <p className="header-subtitle">Review and action flagged messages from Ask Omore</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/ai')} className="back-button">← AI Dashboard</button>
                        {items.length > 0 && (
                            <button
                                onClick={() => {
                                    const csv = exportModerationQueueToCSV(items);
                                    downloadCSV(csv, `omore-moderation-queue-${Date.now()}.csv`);
                                }}
                                className="export-button"
                            >
                                Export CSV
                            </button>
                        )}
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">Sign Out</button>
                    </div>
                </div>
            </header>

            <div className="omore-mod-main">
                {/* Filters */}
                <section className="filters-section">
                    <div className="filter-row">
                        <div className="filter-tabs">
                            {(['unreviewed', 'reviewed', 'all'] as const).map(status => (
                                <button
                                    key={status}
                                    className={`filter-tab ${reviewStatus === status ? 'active' : ''}`}
                                    onClick={() => { setReviewStatus(status); setCurrentPage(0); }}
                                >
                                    {status === 'unreviewed' ? `Unreviewed (${liveCount})` : status.charAt(0).toUpperCase() + status.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="filter-row chips-row">
                        <label className="chips-label">Category:</label>
                        {FLAG_CATEGORIES.map(cat => {
                            const active = selectedCategories.includes(cat);
                            const severity = FlagSeverityMap[cat];
                            const color = SeverityColors[severity];
                            return (
                                <button
                                    key={cat}
                                    className={`chip ${active ? 'active' : ''}`}
                                    style={active ? { background: color, color: 'white', borderColor: color } : {}}
                                    onClick={() => toggleCategory(cat)}
                                >
                                    {CATEGORY_LABELS[cat]}
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* Results info */}
                <div className="results-info">
                    <span>{totalCount} item{totalCount !== 1 ? 's' : ''} found</span>
                </div>

                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading queue...</p>
                    </div>
                )}

                {error && (
                    <div className="error-state">
                        <p>{error}</p>
                        <button onClick={loadQueue} className="retry-button">Retry</button>
                    </div>
                )}

                {!loading && !error && items.length === 0 && (
                    <div className="empty-state">
                        <div className="empty-icon">✓</div>
                        <h3>Queue is clear</h3>
                        <p>No flagged messages matching current filters.</p>
                    </div>
                )}

                {!loading && !error && items.length > 0 && (
                    <>
                        <div className="queue-list">
                            {items.map(item => (
                                <div key={item.id} className="queue-item" style={{ borderLeft: `4px solid ${SeverityColors[item.maxSeverity] || '#9ca3af'}` }}>
                                    <div className="item-header">
                                        <div className="item-info">
                                            <h3 className="item-explorer">{item.explorerName}</h3>
                                            <div className="item-meta">
                                                <span>Helper: {item.helperType}</span>
                                                <span>•</span>
                                                <span>Origin: {item.origin}</span>
                                                <span>•</span>
                                                <span>{formatTimestamp(item.flaggedAt)}</span>
                                            </div>
                                        </div>
                                        {item.reviewAction && (
                                            <span className={`review-badge ${item.reviewAction}`}>{item.reviewAction}</span>
                                        )}
                                    </div>

                                    <div className="item-content">
                                        {item.messageContent.length > 150
                                            ? item.messageContent.slice(0, 150) + '...'
                                            : item.messageContent}
                                    </div>

                                    <div className="item-flags">
                                        {item.flagCategories.map(cat => (
                                            <span
                                                key={cat}
                                                className="flag-tag"
                                                style={{
                                                    background: SeverityColors[FlagSeverityMap[cat]] + '20',
                                                    color: SeverityColors[FlagSeverityMap[cat]],
                                                    border: `1px solid ${SeverityColors[FlagSeverityMap[cat]]}`,
                                                }}
                                            >
                                                {CATEGORY_LABELS[cat]} (Sev {FlagSeverityMap[cat]})
                                            </span>
                                        ))}
                                    </div>

                                    <div className="item-actions">
                                        <button onClick={() => setSelectedItem(item)} className="action-button view-button">
                                            View Details
                                        </button>
                                        {!item.reviewAction && (
                                            <>
                                                <button onClick={() => { setSelectedItem(item); }} className="action-button dismiss-button">
                                                    Dismiss
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    className="page-btn"
                                    disabled={currentPage === 0}
                                    onClick={() => setCurrentPage(p => p - 1)}
                                >
                                    ← Prev
                                </button>
                                <span className="page-info">Page {currentPage + 1} of {totalPages}</span>
                                <button
                                    className="page-btn"
                                    disabled={currentPage >= totalPages - 1}
                                    onClick={() => setCurrentPage(p => p + 1)}
                                >
                                    Next →
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Review Modal */}
            {selectedItem && (
                <div className="modal-overlay" onClick={() => { setSelectedItem(null); setReviewNotes(''); }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Review Flagged Message</h2>
                            <button onClick={() => { setSelectedItem(null); setReviewNotes(''); }} className="close-button">×</button>
                        </div>
                        <div className="modal-body">
                            <div className="review-meta">
                                <div><strong>Explorer:</strong> {selectedItem.explorerName}</div>
                                <div><strong>Helper Type:</strong> {selectedItem.helperType}</div>
                                <div><strong>Origin:</strong> {selectedItem.origin}</div>
                                <div><strong>Flagged At:</strong> {formatTimestamp(selectedItem.flaggedAt)}</div>
                                <div><strong>Session:</strong> <a href={`/omore/sessions/${selectedItem.sessionID}`} onClick={e => { e.preventDefault(); navigate(`/omore/sessions/${selectedItem.sessionID}`); setSelectedItem(null); }}>{selectedItem.sessionID}</a></div>
                            </div>

                            <div className="review-content-box">
                                <strong>Message Content:</strong>
                                <p>{selectedItem.messageContent}</p>
                            </div>

                            <div className="review-flags">
                                {selectedItem.flagCategories.map(cat => (
                                    <span
                                        key={cat}
                                        className="flag-tag"
                                        style={{
                                            background: SeverityColors[FlagSeverityMap[cat]] + '20',
                                            color: SeverityColors[FlagSeverityMap[cat]],
                                            border: `1px solid ${SeverityColors[FlagSeverityMap[cat]]}`,
                                        }}
                                    >
                                        {CATEGORY_LABELS[cat]} — Severity {FlagSeverityMap[cat]}
                                    </span>
                                ))}
                            </div>

                            <div className="review-notes-section">
                                <label>Review Notes (optional)</label>
                                <textarea
                                    value={reviewNotes}
                                    onChange={e => setReviewNotes(e.target.value)}
                                    placeholder="Add context or reasoning for your action..."
                                    rows={3}
                                />
                            </div>

                            <div className="modal-actions">
                                <button
                                    className="modal-button dismiss"
                                    onClick={() => handleReview('dismissed')}
                                    disabled={actionLoading}
                                >
                                    Dismiss
                                </button>
                                <button
                                    className="modal-button warn"
                                    onClick={() => handleReview('warned')}
                                    disabled={actionLoading}
                                >
                                    Warn User
                                </button>
                                <button
                                    className="modal-button block"
                                    onClick={() => handleReview('blocked')}
                                    disabled={actionLoading}
                                >
                                    Block User
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
